import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, FormError } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import type { Tokens } from '../../lib/session.js';

/** Story #4 — exchanges the hosted-UI authorization code for a session. */
export function GoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Authorization codes are single-use; StrictMode double-invokes effects.
  const exchanged = useRef(false);

  const code = params.get('code');
  const oauthError = params.get('error');

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    if (oauthError) {
      setError('Google sign-in was cancelled.');
      return;
    }

    if (!code) {
      setError("That sign-in link didn't work. Head back and try signing in again.");
      return;
    }

    void (async () => {
      try {
        const tokens = await apiRequest<Tokens>('/auth/google/exchange', {
          method: 'POST',
          body: { code, redirectUri: `${window.location.origin}/auth/callback` },
          anonymous: true,
        });
        await adoptTokens(tokens);
        navigate('/polls', { replace: true });
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'Could not complete Google sign-in.',
        );
      }
    })();
  }, [code, oauthError, adoptTokens, navigate]);

  if (error) {
    return (
      <AuthLayout
        title="Sign-in did not complete"
        footer={
          <p className="muted">
            <Link to="/signin">Back to sign in</Link>
          </p>
        }
      >
        <FormError message={error} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Signing you in…" subtitle="One moment.">
      <p className="muted" role="status">
        Completing Google sign-in…
      </p>
    </AuthLayout>
  );
}
